import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Loader2, RefreshCw, Search, ChevronRight, PenTool, Plus } from "lucide-react";
import DetailDrawer from "@/components/DetailDrawer";
import ComposeModal from "@/components/ComposeModal";
import { useAuth } from "@/context/AuthContext";
import { useLivePoll } from "@/hooks/useLivePoll";

/**
 * Generic list viewer for Pit Link resources.
 * Set signOffConfig for supervisor sign-off. Set sendBackConfig for the
 * "Send Back" flow. Set createConfig for the admin "+ New" button.
 */
export default function ResourceList({
  endpoint,
  title,
  subtitle,
  itemLabel,
  itemMeta,
  statusOf,
  priorityOf,
  searchFields = [],
  dataPath,
  emptyLabel = "Nothing here yet.",
  testid,
  signOffConfig,
  sendBackConfig,
  amendConfig,
  createConfig,
  resourceName = "Record",
  sortItems,
  filterItems,
  extraDetail,        // caller can supply a resource-specific detail renderer,
                      // e.g. <ReportDocumentView type="hazard" item={detail} />.
  documentStyle = false, // hides the generic FactList when the caller renders
                         // its own document view.
  detailPath,         // (it) => "/breakdowns/:id" style path. When set, clicking
                      // a card navigates to a full-page view instead of opening
                      // the right-side drawer. Josh Feb 23 spec: Breakdowns and
                      // Handovers must open full-screen for easy reading.
}) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [autoSignoff, setAutoSignoff] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const canCreate = createConfig && canAct(user, createConfig.roles);

  const reload = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get(endpoint);
      let arr = data;
      if (dataPath) {
        for (const k of dataPath.split(".")) arr = arr?.[k];
      }
      if (!Array.isArray(arr)) {
        arr = data?.items || data?.results || data?.data || [];
        if (!Array.isArray(arr)) arr = [];
      }
      setItems(arr);
    } catch (ex) {
      setErr(apiError(ex));
      setItems([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { reload(); }, [endpoint]);
  // Auto-refresh every 5s so records created / signed on the mobile app appear here.
  useLivePoll(reload, 5000, !!endpoint);

  // TOTAL SYNC: whenever the list reloads and the drawer is open, refresh the
  // captured `detail` snapshot from the latest list entry so any field the
  // mobile app just wrote (repair actions, signatures, part swaps, status)
  // shows up in the drawer within one poll cycle. Guarded by id so we don't
  // recreate the object identity if nothing changed.
  useEffect(() => {
    if (!detail || !Array.isArray(items)) return;
    const id = detail.id || detail._id;
    if (!id) return;
    const latest = items.find((it) => (it.id || it._id) === id);
    if (latest && latest !== detail) {
      // Merge so we don't clobber any client-side transient (e.g. optimistic
      // sign-off) — but always prefer the server's newer field values.
      setDetail((cur) => ({ ...cur, ...latest }));
    }
  }, [items]);

  // Deep-link support — Sign-off Inbox drops us here with ?open={id}&signoff=1
  useEffect(() => {
    if (!items || items.length === 0) return;
    const params = new URLSearchParams(location.search);
    const openId = params.get("open");
    if (!openId) return;
    const match = items.find((it) => (it.id || it._id) === openId);
    if (!match) return;
    setDetail(match);
    setAutoSignoff(params.get("signoff") === "1");
    navigate(location.pathname, { replace: true });
  }, [items, location.search, location.pathname, navigate]);

  const filtered = (items || []).filter((it) => {
    if (filterItems && !filterItems(it)) return false;
    if (!q) return true;
    const hay = searchFields.length
      ? searchFields.map((k) => it?.[k] ?? "").join(" ")
      : JSON.stringify(it);
    return hay.toLowerCase().includes(q.toLowerCase());
  });
  const ordered = sortItems ? [...filtered].sort(sortItems) : filtered;

  return (
    <div className="space-y-6" data-testid={testid}>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          {subtitle && (
            <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">
              {subtitle}
            </div>
          )}
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-none mt-1">
            {title}
          </h1>
          <div className="text-muted mt-2 text-sm font-mono-tight">
            {endpoint} · Live from Pit Link
          </div>
        </div>
        <div className="flex gap-2">
          {signOffConfig && (
            <div className="pill pill-orange" data-testid="signoff-available">
              <PenTool className="w-3.5 h-3.5" /> Sign-off enabled
            </div>
          )}
          <button
            className="btn btn-sm btn-ghost"
            onClick={reload}
            disabled={busy}
            data-testid="btn-refresh"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          {canCreate && (
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setShowCreate(true)}
              data-testid="btn-new"
            >
              <Plus className="w-4 h-4" /> New {createConfig.singular || resourceName}
            </button>
          )}
        </div>
      </div>

      <div className="panel-flat p-2.5 flex items-center gap-2">
        <Search className="w-4 h-4 ml-2 text-muted" />
        <input
          className="w-full py-3 outline-none bg-transparent text-inherit"
          placeholder={`Search ${title.toLowerCase()}…`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="list-search"
        />
      </div>

      {err && (
        <div className="pill pill-red !text-sm !py-2.5 !px-4 justify-start" data-testid="list-error">
          {err}
        </div>
      )}

      {items === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading from Pit Link…
        </div>
      ) : filtered.length === 0 ? (
        <div className="panel-flat p-10 text-center text-muted">{emptyLabel}</div>
      ) : (
        <div className="grid gap-3" data-testid="list-items">
          {ordered.map((it, i) => (
            <button
              key={it.id || it._id || i}
              className="panel p-5 md:p-6 text-left flex items-center gap-4 hover:cursor-pointer"
              onClick={() => {
                // When the caller supplied a `detailPath`, route to the full-page
                // view instead of popping the right-side drawer.
                if (detailPath) { navigate(detailPath(it)); return; }
                setDetail(it);
              }}
              data-testid={`item-${it.id || it._id || i}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {statusOf && statusOf(it) && (
                    <span className={`pill ${pillClass(statusOf(it))}`}>{statusOf(it)}</span>
                  )}
                  {priorityOf && priorityOf(it) && (
                    <span className={`pill ${pillClass(priorityOf(it), true)}`}>
                      {priorityOf(it)}
                    </span>
                  )}
                  <span className="text-xs font-mono-tight text-muted">
                    #{String(it.id || it._id || "").slice(0, 8) || i + 1}
                  </span>
                </div>
                <div className="font-display font-black text-lg md:text-xl truncate">
                  {itemLabel ? itemLabel(it) : it.title || it.name || it.summary || "(no title)"}
                </div>
                {itemMeta && (
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm">
                    {itemMeta(it).map((m, k) => (
                      <span key={k}>
                        <span className="text-[10px] uppercase tracking-widest text-muted">{m.label}: </span>
                        <span className="font-mono-tight">{m.value ?? "—"}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-muted shrink-0" />
            </button>
          ))}
        </div>
      )}

      <DetailDrawer
        item={detail}
        onClose={() => { setDetail(null); setAutoSignoff(false); }}
        onUpdated={() => reload()}
        itemLabel={itemLabel}
        signOffConfig={signOffConfig}
        sendBackConfig={sendBackConfig}
        amendConfig={amendConfig}
        resourceName={resourceName}
        openSignOffOnMount={autoSignoff}
        extraDetail={extraDetail && detail ? extraDetail(detail) : null}
        documentStyle={documentStyle}
      />

      {createConfig && (
        <ComposeModal
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={(data) => {
            setShowCreate(false);
            // Let the calling page act on the freshly-created record
            // (e.g. Breakdowns cross-writes SMU into the matching inspection).
            createConfig.onCreated?.(data);
            reload();
          }}
          title={createConfig.title || `New ${createConfig.singular || resourceName}`}
          endpoint={createConfig.endpoint || endpoint}
          fields={createConfig.fields || []}
          initial={createConfig.initial || {}}
          topSlot={createConfig.topSlot || null}
          testid={`${testid || "list"}-create`}
        />
      )}
    </div>
  );
}

function pillClass(v, isPriority = false) {
  const s = String(v).toLowerCase();
  if (s.includes("assigned to me") || s.includes("to me") || s.includes("mine"))
    return "pill-orange-solid";
  if (["open", "pending", "waiting", "queued", "new", "draft", "await"].some((k) => s.includes(k)))
    return "pill-orange";
  if (["progress", "in_progress", "active", "amend", "actioned"].some((k) => s.includes(k)))
    return "pill-orange-solid";
  if (["closed", "done", "complete", "approved", "signed", "ok", "resolved"].some((k) => s.includes(k)))
    return "pill-green";
  if (["reject", "fail", "down", "critical", "high"].some((k) => s.includes(k)))
    return isPriority ? "pill-red-solid" : "pill-red";
  return "pill";
}

function canAct(user, roles) {
  if (!user) return false;
  if (!roles || roles.length === 0) return true;
  const r = String(user.role || "").toLowerCase();
  return roles.some((x) => r.includes(x));
}
